/**
 * Entry-count bound for module-level Maps used as caches.
 *
 * OWA-671: several caches across the client are written and read but never
 * cleared, so they grow for the lifetime of the tab. Each one is individually
 * small; together they are a steady ratchet on heap in a long-lived session.
 *
 * Eviction is FIFO over insertion order (which Map guarantees), with the
 * position refreshed on write. It is NOT a true LRU — reads do not promote an
 * entry — because every caller here is a pure memoisation whose worst case on
 * eviction is a recompute or a refetch, never a wrong answer. Reads outnumber
 * writes in these caches, so tracking read order would cost more than the miss
 * it avoids.
 *
 * Do NOT use this for a cache whose stale entry is load-bearing for
 * correctness. `FinancialHelper`'s invoice-info cache is the standing example:
 * its deps hash covers only entity IDs, so for a finalized invoice with no
 * `finalValues` snapshot the cached entry is acting as a freeze, and evicting
 * it can change the figures shown on a legal document.
 */

/**
 * Insert into `cache`, evicting oldest entries until it is within `maxEntries`.
 *
 * @param cache      the Map to write into
 * @param key        entry key
 * @param value      entry value
 * @param maxEntries hard ceiling on entry count; must be >= 1
 */
export function setBounded<K, V>(cache: Map<K, V>, key: K, value: V, maxEntries: number): void {
    // Delete before set so an overwrite moves the key to the end. Without this
    // a hot key keeps its original position and can be evicted ahead of entries
    // that have not been touched since they were created.
    if (cache.has(key)) cache.delete(key);

    cache.set(key, value);

    while (cache.size > maxEntries) {
        const oldest = cache.keys().next();

        // Defensive: an empty Map cannot exceed a >= 1 cap, but never spin.
        if (oldest.done) break;

        cache.delete(oldest.value);
    }
}

export default setBounded;
